export type SiteType = 'pit' | 'yard' | 'rockfall';
export type Owner = 'MoTI' | 'DRM' | 'Private / Third-Party' | 'Unknown';

/**
 * Where a bin stands against its band, worst first: below zero (a count is overdue), below the
 * minimum, above the maximum, fine. `empty` is a bin holding no material.
 */
export type BinStatus = 'negative' | 'below-min' | 'over-max' | 'ok' | 'empty';

export interface Bin {
  /** Bin id — a code like "Bin 1" is only unique inside its location. */
  id: string;
  name: string;
  /** "103L (TREATED 9.5mm Winter Sand)". Empty when the bin holds nothing. */
  material: string;
  materialId: string | null;
  unitOfMeasure: string | null;
  owner: string | null;
  /** On hand, derived from the ledger. Can be below zero. */
  quantity: number;
  minQuantity: number | null;
  maxQuantity: number | null;
  status: BinStatus;
}

export interface Location {
  id: string;
  name: string;
  /** Raw StorageLocationType, e.g. SHED. Use locationKindLabel to display it. */
  type: string;
  bins: Bin[];
}

/** How many of a site's bins need someone to act, by reason. */
export interface SiteAttention {
  negative: number;
  belowMin: number;
  overMax: number;
}

export interface Site {
  id: string;
  type: SiteType;
  name: string;
  /** Shown as "#684". Omit the badge when null. */
  number: string | null;
  owner: Owner;
  /** Null when the site has no recorded position; the map omits it. */
  latitude: number | null;
  longitude: number | null;
  /** Address or directions, shown under the name. */
  description: string | null;
  /** Optional data caveat. */
  note: string | null;
  serviceAreaId: string;
  /** "Service Area 16". */
  serviceAreaName: string;
  /** The foreman areas whose crews draw from this site. Empty until mapped. */
  foremanAreaIds: string[];
  attention: SiteAttention;
  /** Newest count on any bin here; null if never counted. */
  lastCountAt: string | null;
  locations: Location[];
}

/** The map's type filter. Rockfall routes hold no stock and are not on this page. */
export type TypeFilter = 'all' | SiteType;

/** Which bins to show: any, or only those needing action (optionally for one reason). */
export type AttentionFilter = 'any' | 'needs-action' | 'negative' | 'below-min' | 'over-max';

/** Everything the Locations list is filtered by. All lists empty = no filter. */
export interface SiteFilters {
  serviceAreaIds: string[];
  foremanAreaIds: string[];
  /** Sites with a bin holding any of these materials. */
  materialIds: string[];
  types: Array<Exclude<SiteType, 'rockfall'>>;
  owners: Owner[];
  attention: AttentionFilter;
  search: string;
}

export const EMPTY_FILTERS: SiteFilters = {
  serviceAreaIds: [],
  foremanAreaIds: [],
  materialIds: [],
  types: [],
  owners: [],
  attention: 'any',
  search: '',
};

export type SiteSortKey =
  | 'name'
  | 'type'
  | 'owner'
  | 'serviceArea'
  | 'foremanArea'
  | 'locations'
  | 'bins'
  | 'materials'
  | 'attention'
  | 'lastCount';

export interface SiteSort {
  key: SiteSortKey;
  dir: 'asc' | 'desc';
}

export const OWNERS: Owner[] = ['MoTI', 'DRM', 'Private / Third-Party', 'Unknown'];

export const TYPE_LABEL: Record<SiteType, string> = {
  pit: 'Pit',
  yard: 'Yard',
  rockfall: 'Rockfall route',
};

/**
 * Drill-through shapes (site page). Kept apart from the list shapes above: here every
 * location and bin has an id, because a bin code ("Bin 1") is only unique inside its
 * location and a location name is not unique at all. Bins are keyed and linked by id,
 * and the material belongs to the bin, never to the location.
 */
export interface BinMaterial {
  id: string;
  code: string | null;
  description: string | null;
  /** e.g. "m3". The unit every quantity on the bin is expressed in. */
  unitOfMeasure: string | null;
  /** Material type from the controlled list, e.g. "Aggregate - Sand". Groups the material picker. */
  type: string | null;
}

export interface StorageBin {
  id: string;
  code: string;
  owner: string | null;
  material: BinMaterial | null;
  /**
   * On hand in the material's unit, derived from the ledger. Can be below zero: a draw that
   * overshot is saved and flagged rather than refused, and a count corrects it.
   */
  quantity: number;
  /** Sent back with a count, so a movement saved while counting is reported, not overwritten. */
  ledgerVersion: number;
  /** Cost per unit for this bin; null when none is on file. */
  unitCost: number | null;
  /** Reorder band for the on-hand bar; null when not set. */
  minQuantity: number | null;
  maxQuantity: number | null;
}

export interface StorageLocation {
  id: string;
  name: string;
  /** Raw enum value from the API, e.g. STOCKPILE. Use locationKindLabel to display it. */
  kind: string;
  /** ISO timestamp of the latest COUNT on any bin here, or null when none yet. */
  lastCountAt: string | null;
  bins: StorageBin[];
}

export interface SiteWithLocations {
  id: string;
  type: SiteType;
  name: string;
  number: string | null;
  owner: Owner;
  latitude: number | null;
  longitude: number | null;
  description: string | null;
  /** Where the site sits; the edit form starts from it. */
  serviceAreaId: string;
  /** The foreman areas whose crews draw from this site. */
  foremanAreaIds: string[];
  locations: StorageLocation[];
}

/**
 * MT-C04 A quality certificate (sieve analysis and the like) on a bin. A bin keeps every
 * certificate it has had, each with the date on it and a description.
 */
export interface MaterialCertificate {
  id: string;
  binId: string;
  /** "Bin 2". */
  binCode: string;
  /** The bin's location, for grouping. */
  storageLocationId: string;
  /** The material it certified (stays put if the bin later holds something else). */
  materialId: string;
  /** "110 (19/25mm Crush, High Fines)", the same label a bin shows. */
  materialLabel: string;
  description: string;
  reference: string | null;
  /** The date on the certificate: calendar date, YYYY-MM-DD. */
  issuedAt: string;
  /** Calendar date, YYYY-MM-DD, or null when it does not expire. */
  expiresAt: string | null;
  fileName: string;
  uploadedBy: string | null;
  createdAt: string;
}
